"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { ManageTeamsModal } from "./ManageTeamsModal";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Helper pour obtenir le label du statut
function getStatusLabel(status, t) {
    switch (status) {
        case "trialing":
            return t("teamSwitcher.statusTrial");
        case "suspended":
            return t("teamSwitcher.statusSuspended");
        case "past_due":
            return t("teamSwitcher.statusPastDue");
        case "canceled":
            return t("teamSwitcher.statusCanceled");
        default:
            return null;
    }
}

// Helper pour tronquer le nom de la team
function truncateName(name, maxLength = 15) {
    if (!name) return "";
    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
}

// Helper pour obtenir les styles du badge de statut
function getStatusStyles(status) {
    switch (status) {
        case "trialing":
            return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
        case "suspended":
            return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
        case "past_due":
            return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
        case "canceled":
            return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
        default:
            return "";
    }
}

// Composant pour l'icône de team (réutilisable)
function TeamIcon({ team, size = "md" }) {
    const sizeClasses = {
        sm: "w-5 h-5 text-xs rounded-md",
        md: "w-9 h-9 text-sm rounded-lg",
        lg: "w-10 h-10 text-base rounded-lg",
    };

    const iconSize = sizeClasses[size] || sizeClasses.md;
    const [w, h, , rounded] = iconSize.split(" ");

    if (team.iconUrl) {
        return (
            <img
                src={team.iconUrl}
                alt={team.name}
                className={`${w} ${h} ${rounded} object-cover shadow-sm`}
            />
        );
    }

    return (
        <div className={`${w} ${h} ${rounded} bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm`}>
            <span className={`text-white font-semibold ${iconSize.split(" ")[2] || "text-sm"}`}>
                {team.name?.[0]?.toUpperCase() || "?"}
            </span>
        </div>
    );
}

export function TeamSwitcher({ currentTeam, availableTeams = [], showCreateTeam = true, size = "default", dropdownPosition = "bottom", useModal = false, fullWidth = false, iconOnly = false }) {
    const t = useTranslations("layout");
    const [isOpen, setIsOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [switchingTeamId, setSwitchingTeamId] = useState(null);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Doit rester avant tout early return (règle des Hooks React).
    useEffect(() => {
        // En mode modale, le backdrop gère déjà la fermeture sur clic extérieur.
        if (useModal) return;

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [useModal]);

    // Si pas de currentTeam, ne rien afficher
    if (!currentTeam) {
        return null;
    }

    // Utiliser les vraies teams ou juste la currentTeam
    const teams = availableTeams.length > 0 ? availableTeams : [currentTeam];
    const current = currentTeam;

    const handleTeamSwitch = async (team) => {
        if (team.id === current.id || switching) {
            setIsOpen(false);
            return;
        }

        setSwitching(true);
        setSwitchingTeamId(team.id);

        try {
            const response = await fetch("/api/teams/switch", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ teamId: team.id }),
            });

            if (response.ok) {
                // Rediriger vers settings si la team est canceled ou past_due (pour réactiver/payer)
                if (team.teamStatus === "canceled" || team.teamStatus === "past_due") {
                    window.location.href = "/teams/dashboard/settings";
                } else {
                    window.location.href = "/teams/dashboard";
                }
            } else {
                setSwitching(false);
                setSwitchingTeamId(null);
            }
        } catch (error) {
            console.error("Erreur lors du changement d'équipe:", error);
            setSwitching(false);
            setSwitchingTeamId(null);
        }
    };

    // Ne montrer le dropdown que s'il y a plusieurs teams ou si on peut créer une team
    const showDropdown = teams.length > 1 || showCreateTeam;
    const isCompact = size === "compact";

    return (
        <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={dropdownRef}>
            {iconOnly ? (
                showDropdown ? (
                    <button
                        onClick={() => {
                            if (useModal) {
                                setIsManageModalOpen(true);
                            } else {
                                setIsOpen(!isOpen);
                            }
                        }}
                        className="flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-all duration-200 p-1"
                        title={current.name}
                        aria-label={current.name}
                    >
                        <TeamIcon team={current} size="md" />
                    </button>
                ) : (
                    <div
                        className="flex items-center justify-center p-1"
                        title={current.name}
                    >
                        <TeamIcon team={current} size="md" />
                    </div>
                )
            ) : showDropdown ? (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center rounded-lg hover:bg-gray-50 dark:hover:bg-grayDark-700 transition-all duration-200 ${
                        isCompact ? "gap-2 px-3 py-2" : fullWidth ? "gap-3 py-2" : "gap-3 px-3 py-2"
                    } ${fullWidth ? "w-full" : ""}`}
                >
                    <TeamIcon team={current} size={isCompact ? "sm" : "lg"} />
                    <div className="text-left flex-1">
                        <div className="flex items-center gap-2">
                            <p className={`font-semibold text-gray-900 dark:text-grayDark-100 mb-0 leading-tight ${
                                isCompact ? "text-sm" : "text-base font-bold"
                            }`} title={current.name}>
                                {truncateName(current.name)}
                            </p>
                            {isCompact && current.teamStatus && current.teamStatus !== "active" && (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getStatusStyles(current.teamStatus)}`}>
                                    {getStatusLabel(current.teamStatus, t)}
                                </span>
                            )}
                        </div>
                        {!isCompact && (
                            <p className={`text-xs mb-0 mt-0.5 leading-tight ${
                                current.teamStatus === "trialing"
                                    ? "text-blue-600 dark:text-blue-400"
                                    : current.teamStatus === "suspended"
                                    ? "text-orange-600 dark:text-orange-400"
                                    : current.teamStatus === "past_due"
                                    ? "text-red-600 dark:text-red-400"
                                    : current.teamStatus === "canceled"
                                    ? "text-red-600 dark:text-red-400"
                                    : "text-gray-500 dark:text-grayDark-400"
                            }`}>
                                {current.teamStatus === "trialing"
                                    ? t("teamSwitcher.statusTrialPeriod")
                                    : current.teamStatus === "suspended"
                                    ? t("teamSwitcher.statusSuspended")
                                    : current.teamStatus === "past_due"
                                    ? t("teamSwitcher.statusPastDue")
                                    : current.teamStatus === "canceled"
                                    ? t("teamSwitcher.statusCanceled")
                                    : current.role
                                    ? current.role === "owner"
                                        ? t("teamSwitcher.roleOwner")
                                        : current.role === "manager"
                                        ? t("teamSwitcher.roleManager")
                                        : t("teamSwitcher.roleMember")
                                    : t("teamSwitcher.team")}
                            </p>
                        )}
                    </div>
                    <SolarIcon name="alt-arrow-down-linear"
                        className={`text-gray-400 transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                        } ${isCompact ? "w-3 h-3" : "w-4 h-4"}`}
                    />
                </button>
            ) : (
                <div className={`flex items-center ${isCompact ? "gap-2" : "gap-3"} ${fullWidth ? "w-full py-2" : ""}`}>
                    <TeamIcon team={current} size={isCompact ? "sm" : "lg"} />
                    <div className="text-left">
                        <div className="flex items-center gap-2">
                            <p className={`font-semibold text-gray-900 dark:text-grayDark-100 mb-0 leading-tight ${
                                isCompact ? "text-sm" : "text-base font-bold"
                            }`} title={current.name}>
                                {truncateName(current.name)}
                            </p>
                            {isCompact && current.teamStatus && current.teamStatus !== "active" && (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getStatusStyles(current.teamStatus)}`}>
                                    {getStatusLabel(current.teamStatus, t)}
                                </span>
                            )}
                        </div>
                        {!isCompact && (
                            <p className={`text-xs mb-0 mt-0.5 leading-tight ${
                                current.teamStatus === "trialing"
                                    ? "text-blue-600 dark:text-blue-400"
                                    : current.teamStatus === "suspended"
                                    ? "text-orange-600 dark:text-orange-400"
                                    : current.teamStatus === "past_due"
                                    ? "text-red-600 dark:text-red-400"
                                    : current.teamStatus === "canceled"
                                    ? "text-red-600 dark:text-red-400"
                                    : "text-gray-500 dark:text-grayDark-400"
                            }`}>
                                {current.teamStatus === "trialing"
                                    ? t("teamSwitcher.statusTrialPeriod")
                                    : current.teamStatus === "suspended"
                                    ? t("teamSwitcher.statusSuspended")
                                    : current.teamStatus === "past_due"
                                    ? t("teamSwitcher.statusPastDue")
                                    : current.teamStatus === "canceled"
                                    ? t("teamSwitcher.statusCanceled")
                                    : current.role
                                    ? current.role === "owner"
                                        ? t("teamSwitcher.roleOwner")
                                        : current.role === "manager"
                                        ? t("teamSwitcher.roleManager")
                                        : t("teamSwitcher.roleMember")
                                    : t("teamSwitcher.team")}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {isOpen && showDropdown && !useModal && (
                <div className={`absolute w-72 z-50 animate-fadeIn ${
                    dropdownPosition === "top"
                        ? "bottom-full left-0 mb-2"
                        : "top-full left-0 mt-2"
                }`}>
                    {/* Flèche du dropdown */}
                    {dropdownPosition !== "top" && (
                        <div className="flex justify-start pl-6">
                            <div className="w-3 h-3 bg-white dark:bg-grayDark-800 border-l border-t border-gray-200 dark:border-grayDark-700 transform rotate-45 -mb-1.5 z-10"></div>
                        </div>
                    )}
                    <div className="bg-white dark:bg-grayDark-800 rounded-xl shadow-xl border border-gray-200 dark:border-grayDark-700">
                    <div className="p-3">
                        <div className="flex items-center justify-between px-3 py-2">
                            <p className="text-xs font-semibold text-gray-400 dark:text-grayDark-500 uppercase tracking-wider mb-0">
                                {t("teamSwitcher.yourTeams")}
                            </p>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    setIsManageModalOpen(true);
                                }}
                                className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-colors group"
                                title={t("teamSwitcher.manageTeams")}
                            >
                                <SolarIcon name="settings-linear" className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-grayDark-300 transition-colors" />
                            </button>
                        </div>

                        <div className="space-y-1">
                            {teams.map((team) => (
                                <button
                                    key={team.id}
                                    onClick={() => handleTeamSwitch(team)}
                                    disabled={switching}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
                                        team.id === current.id
                                            ? "bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30"
                                            : "hover:bg-gray-50 dark:hover:bg-grayDark-700 border border-transparent"
                                    } ${switching && switchingTeamId !== team.id ? "opacity-50" : ""}`}
                                >
                                    <div className="flex items-center gap-3">
                                        {switchingTeamId === team.id ? (
                                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <SolarIcon name="refresh-linear" className="w-5 h-5 text-primary animate-spin" />
                                            </div>
                                        ) : (
                                            <TeamIcon team={team} size="md" />
                                        )}
                                        <div className="text-left">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-grayDark-100 mb-0 leading-tight" title={team.name}>
                                                    {truncateName(team.name)}
                                                </p>
                                                {team.teamStatus && team.teamStatus !== "active" && (
                                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getStatusStyles(team.teamStatus)}`}>
                                                        {getStatusLabel(team.teamStatus, t)}
                                                    </span>
                                                )}
                                            </div>
                                            {team.role && (
                                                <p className="text-xs text-gray-500 dark:text-grayDark-400 mb-0 mt-0.5 leading-tight">
                                                    {switchingTeamId === team.id
                                                        ? t("teamSwitcher.loading")
                                                        : team.role === "owner"
                                                        ? t("teamSwitcher.roleOwner")
                                                        : team.role === "manager"
                                                        ? t("teamSwitcher.roleManager")
                                                        : t("teamSwitcher.roleMember")}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {team.id === current.id && !switching && (
                                        <SolarIcon name="unread-linear" className="w-4 h-4 text-primary flex-shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {showCreateTeam && (
                        <div className="border-t border-gray-100 dark:border-grayDark-700 p-3">
                            <Link
                                href="/teams/signup"
                                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-primary/5 dark:hover:bg-primary/10 rounded-lg transition-all duration-200 group"
                            >
                                <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
                                    <SolarIcon name="add-linear" className="w-4 h-4 text-primary" />
                                </div>
                                <span className="text-sm text-primary font-semibold">
                                    {t("teamSwitcher.createTeam")}
                                </span>
                            </Link>
                        </div>
                    )}
                    </div>
                </div>
            )}

            {/* Modal version */}
            <BottomSheetModal
                isOpen={isOpen && showDropdown && useModal}
                onClose={() => setIsOpen(false)}
                maxWidthClass="sm:max-w-sm"
                zIndexClass="z-[9999]"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-grayDark-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-grayDark-100">
                        {t("teamSwitcher.yourTeams")}
                    </h3>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                setIsManageModalOpen(true);
                            }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-colors group"
                            title={t("teamSwitcher.manageTeams")}
                        >
                            <SolarIcon name="settings-linear" className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-grayDark-300 transition-colors" />
                        </button>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-colors"
                        >
                            <SolarIcon name="close-circle-linear" className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Teams list */}
                <div className="p-3 max-h-80 overflow-y-auto">
                    <div className="space-y-1">
                        {teams.map((team) => (
                            <button
                                key={team.id}
                                onClick={() => handleTeamSwitch(team)}
                                disabled={switching}
                                className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 ${
                                    team.id === current.id
                                        ? "bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30"
                                        : "hover:bg-gray-50 dark:hover:bg-grayDark-700 border border-transparent"
                                } ${switching && switchingTeamId !== team.id ? "opacity-50" : ""}`}
                            >
                                <div className="flex items-center gap-3">
                                    {switchingTeamId === team.id ? (
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <SolarIcon name="refresh-linear" className="w-5 h-5 text-primary animate-spin" />
                                        </div>
                                    ) : (
                                        <TeamIcon team={team} size="lg" />
                                    )}
                                    <div className="text-left">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-grayDark-100 mb-0 leading-tight" title={team.name}>
                                                {truncateName(team.name)}
                                            </p>
                                            {team.teamStatus && team.teamStatus !== "active" && (
                                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getStatusStyles(team.teamStatus)}`}>
                                                    {getStatusLabel(team.teamStatus, t)}
                                                </span>
                                            )}
                                        </div>
                                        {team.role && (
                                            <p className="text-xs text-gray-500 dark:text-grayDark-400 mb-0 mt-0.5 leading-tight">
                                                {switchingTeamId === team.id
                                                    ? "Chargement..."
                                                    : team.role === "owner"
                                                    ? "Propriétaire"
                                                    : team.role === "manager"
                                                    ? "Manager"
                                                    : "Membre"}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {team.id === current.id && !switching && (
                                    <SolarIcon name="unread-linear" className="w-5 h-5 text-primary flex-shrink-0" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {showCreateTeam && (
                    <div className="border-t border-gray-100 dark:border-grayDark-700 p-3">
                        <Link
                            href="/teams/signup"
                            className="w-full flex items-center gap-3 px-3 py-3 hover:bg-primary/5 dark:hover:bg-primary/10 rounded-xl transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
                                <SolarIcon name="add-linear" className="w-5 h-5 text-primary" />
                            </div>
                            <span className="text-sm text-primary font-semibold">
                                {t("teamSwitcher.createTeam")}
                            </span>
                        </Link>
                    </div>
                )}
            </BottomSheetModal>

            {/* Modal de gestion des équipes */}
            <ManageTeamsModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
                teams={teams}
                currentTeamId={current.id}
            />
        </div>
    );
}
